import type { UserPresence } from "@bezoned/shared/types/api/user/user-presence-get.types.js";
import type { UserAvailability } from "@bezoned/shared/types/user-presence.js";
import { Tree } from "fluid-framework";
import { isEqual } from "lodash";
import type { ContextType } from "react";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  userPresenceGet,
  userPresenceSet,
  userStatusMessageSet,
} from "../../services/api.service.js";
import { recordErrorInSentry } from "../../utils/init-sentry.js";
import { useAuth } from "../auth/use-auth.js";
import {
  getUser,
  getUserPresenceFromFluid,
  updateUserAvailabilityInFluid,
  updateUserPresenceInFluid,
  updateUserStatusMessageInFluid,
} from "../fluid/fluid.service.js";
import { useFluid } from "../fluid/hooks/use-fluid.js";
import { PresenceContext } from "./presence.context.js";

const SYNC_INTERVAL_MS = 15_000;
const PULL_STALE_GUARD_AFTER_MUTATION_MS = 20_000;

export const PresenceProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { user } = useAuth();
  const { sharedTree, fluidUserId } = useFluid();
  const lifecycle = useMemo(
    () => ({ fluidUserId, sharedTree, userId: user?.id }),
    [fluidUserId, sharedTree, user?.id]
  );
  const lifecycleRef = useRef<typeof lifecycle | null>(lifecycle);
  const [resolution, setResolution] = useState<{
    lifecycle: typeof lifecycle;
    availability: UserAvailability | null;
    error: unknown;
  }>({ availability: null, error: null, lifecycle });
  const currentAvailability =
    resolution.lifecycle === lifecycle ? resolution.availability : null;
  const presenceError =
    resolution.lifecycle === lifecycle ? resolution.error : null;
  let presenceStatus: "loading" | "ready" | "error" = "loading";
  if (currentAvailability !== null) {
    presenceStatus = "ready";
  } else if (presenceError) {
    presenceStatus = "error";
  }
  const [presenceState, setPresenceState] = useState<{
    lifecycle: typeof lifecycle;
    presence: UserPresence | null;
  }>({ lifecycle, presence: null });
  const presence =
    presenceState.lifecycle === lifecycle ? presenceState.presence : null;
  const presenceRef = useRef(presence);
  const lastLocalMutationAtRef = useRef(0);
  const mutationRevisionRef = useRef(0);
  const mutationQueueRef = useRef<Promise<unknown>>(Promise.resolve());
  const setPresenceIfChanged = useCallback(
    (nextPresence: UserPresence) => {
      if (lifecycleRef.current !== lifecycle) {
        return;
      }
      presenceRef.current = nextPresence;

      setPresenceState((previous) =>
        previous.lifecycle === lifecycle &&
        isEqual(previous.presence, nextPresence)
          ? previous
          : { lifecycle, presence: nextPresence }
      );
    },
    [lifecycle]
  );

  const enqueuePresenceMutation = useCallback(
    <T,>(mutation: () => Promise<T>): Promise<T> => {
      const previous = mutationQueueRef.current;
      const run = async () => {
        await previous;
        return mutation();
      };
      const result = run();
      const settle = async () => {
        try {
          await result;
        } catch {
          return null;
        }
      };
      mutationQueueRef.current = settle();
      return result;
    },
    []
  );

  useLayoutEffect(() => {
    lifecycleRef.current = lifecycle;
    presenceRef.current = null;
    lastLocalMutationAtRef.current = 0;
    mutationQueueRef.current = Promise.resolve();

    return () => {
      if (lifecycleRef.current === lifecycle) {
        lifecycleRef.current = null;
      }
    };
  }, [lifecycle]);

  // Sync fluid from Graph API (immediately on mount and then on interval)
  useEffect(() => {
    // Only run when we have everything needed
    if (!(fluidUserId && sharedTree && user?.id)) {
      return;
    }

    let cancelled = false;
    let inFlight = false;
    const isCurrent = () => !cancelled && lifecycleRef.current === lifecycle;

    const syncPresenceOnce = async () => {
      if (inFlight || !isCurrent()) {
        return;
      }
      inFlight = true;
      const mutationAtStart = mutationRevisionRef.current;
      try {
        const graphApiPresence = await userPresenceGet({ msUserId: user.id });
        if (!isCurrent() || mutationAtStart !== mutationRevisionRef.current) {
          return;
        }

        const hasRecentLocalMutation =
          Date.now() - lastLocalMutationAtRef.current <=
          PULL_STALE_GUARD_AFTER_MUTATION_MS;
        if (
          hasRecentLocalMutation &&
          !isEqual(graphApiPresence, presenceRef.current)
        ) {
          console.info(
            "[presence] skipped graph pull due to recent local mutation",
            {
              sinceMutationMs: Date.now() - lastLocalMutationAtRef.current,
            }
          );
          return;
        }

        updateUserPresenceInFluid({
          presence: graphApiPresence,
          sharedTree,
          userId: fluidUserId,
        });
        setPresenceIfChanged(graphApiPresence);
        setResolution({
          availability: graphApiPresence.availability,
          error: null,
          lifecycle,
        });
        console.info("[presence] current presence resolved", {
          availability: graphApiPresence.availability,
          fluidUserId,
        });
      } catch (error) {
        if (!isCurrent()) {
          return;
        }
        setResolution((previous) => ({
          availability:
            previous.lifecycle === lifecycle ? previous.availability : null,
          error,
          lifecycle,
        }));
        recordErrorInSentry(error);
        console.warn(
          "[presence] Failed to sync presence from Graph API:",
          error
        );
      } finally {
        inFlight = false;
      }
    };

    // Immediate sync on mount
    syncPresenceOnce();

    // Periodic sync
    const intervalId = setInterval(syncPresenceOnce, SYNC_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [sharedTree, fluidUserId, user?.id, lifecycle, setPresenceIfChanged]);

  // Listen for Fluid changes and propagate them to state that's shared
  // with children
  useEffect(() => {
    let unsubscribe: (() => void) | null = null;

    const updatePresenceFromFluid = () => {
      if (fluidUserId && sharedTree) {
        const newPresence = getUserPresenceFromFluid({
          sharedTree,
          userId: fluidUserId,
        });

        if (!newPresence) {
          console.warn(
            "[PresenceProvider] No presence found in Fluid. Skipping update."
          );
          return;
        }

        // ? "as" is needed in this case, because Fluid does not allow enums and we have enum for availability
        setPresenceIfChanged(newPresence as UserPresence);
      }
    };

    const registerFluidListener = () => {
      if (fluidUserId && sharedTree) {
        const fluidUser = getUser({ sharedTree, userId: fluidUserId });
        if (!fluidUser) {
          console.warn(
            "[PresenceProvider] No user found in Fluid. Skipping update."
          );
          return;
        }

        unsubscribe = Tree.on(
          fluidUser,
          "treeChanged",
          updatePresenceFromFluid
        );

        // Initialize default presence
        const fluidPresence = getUserPresenceFromFluid({
          sharedTree,
          userId: fluidUserId,
        });

        if (!fluidPresence) {
          console.warn(
            "[PresenceProvider] No presence found in Fluid. Skipping update."
          );
          return;
        }

        // ? "as" is needed in this case, because Fluid does not allow enums and we have enum for availability
        setPresenceIfChanged(fluidPresence as UserPresence);
      }
    };

    registerFluidListener();

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [fluidUserId, setPresenceIfChanged, sharedTree]);

  const updateAvailability = useCallback(
    (availability: UserAvailability) =>
      enqueuePresenceMutation(async () => {
        if (lifecycleRef.current !== lifecycle) {
          return;
        }
        if (!presenceRef.current) {
          throw new Error(
            "Cannot update availability: presence is not loaded yet"
          );
        }

        const newPresenceData = {
          ...presenceRef.current,
          availability,
        };

        await userPresenceSet(newPresenceData);
        if (lifecycleRef.current !== lifecycle) {
          return;
        }
        lastLocalMutationAtRef.current = Date.now();
        mutationRevisionRef.current += 1;

        if (fluidUserId && sharedTree) {
          updateUserAvailabilityInFluid({
            availability,
            sharedTree,
            userId: fluidUserId,
          });
        }

        // ? "as" is needed in this case, because Fluid does not allow enums and we have enum for availability
        setPresenceIfChanged(newPresenceData as UserPresence);
        setResolution({ availability, error: null, lifecycle });
      }),
    [
      enqueuePresenceMutation,
      fluidUserId,
      lifecycle,
      setPresenceIfChanged,
      sharedTree,
    ]
  );

  const updateStatusMessage = useCallback(
    (statusMessage: string | null) =>
      enqueuePresenceMutation(async () => {
        if (lifecycleRef.current !== lifecycle) {
          return;
        }
        await userStatusMessageSet({ statusMessage });
        if (lifecycleRef.current !== lifecycle) {
          return;
        }
        lastLocalMutationAtRef.current = Date.now();
        mutationRevisionRef.current += 1;

        if (fluidUserId && sharedTree) {
          updateUserStatusMessageInFluid({
            sharedTree,
            statusMessage,
            userId: fluidUserId,
          });
        }

        if (presenceRef.current) {
          setPresenceIfChanged({ ...presenceRef.current, statusMessage });
        }
      }),
    [
      enqueuePresenceMutation,
      fluidUserId,
      lifecycle,
      setPresenceIfChanged,
      sharedTree,
    ]
  );

  const contextValue = useMemo<ContextType<typeof PresenceContext>>(
    () => ({
      __ctx_init: true,
      availability: currentAvailability,
      presence,
      presenceError,
      presenceStatus,
      updateAvailability,
      updateStatusMessage,
    }),
    [
      presence,
      currentAvailability,
      presenceStatus,
      presenceError,
      updateAvailability,
      updateStatusMessage,
    ]
  );

  return (
    <PresenceContext.Provider value={contextValue}>
      {children}
    </PresenceContext.Provider>
  );
};
